import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  EntradaCrearUsuario,
  EntradaEditarUsuario,
  RespuestaActivacion,
  RespuestaInvitacion,
  ResultadoReenvio,
  Usuario,
} from '@copathy/shared';
import { api } from './api';

export type PaginaUsuarios = {
  datos: Usuario[];
  total: number;
  pagina: number;
  porPagina: number;
};

export type FiltrosUsuarios = Record<string, string | undefined>;

/**
 * Todos los filtros viajan en la misma bolsa de claves sueltas, que es lo que
 * maneja el `BuscadorFiltros`. Se copian tal cual a la query: nada que traducir,
 * y el estado de la pantalla es literalmente lo que va en la URL.
 */
const CLAVES_FILTRO = [
  'ambito',
  'rol',
  'estado',
  'empresaId',
  'registroDesde',
  'registroHasta',
  'accesoDesde',
  'accesoHasta',
  'invitacionPendiente',
] as const;

export function useUsuarios(texto: string, filtros: FiltrosUsuarios) {
  const parametros = new URLSearchParams({ porPagina: '200' });
  if (texto.trim()) parametros.set('texto', texto.trim());
  for (const clave of CLAVES_FILTRO) {
    const valor = filtros[clave];
    if (valor) parametros.set(clave, valor);
  }

  return useQuery({
    queryKey: ['usuarios', texto.trim(), filtros],
    queryFn: () => api.get<PaginaUsuarios>(`/api/usuarios?${parametros.toString()}`),
    placeholderData: (anterior) => anterior,
  });
}

/** Ficha de una cuenta, para su perfil. */
export function useUsuario(id: number | null) {
  return useQuery({
    queryKey: ['usuario', id],
    queryFn: () => api.get<Usuario>(`/api/usuarios/${id!}`),
    enabled: id !== null,
  });
}

function usarInvalidacion() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['usuarios'] });
    void qc.invalidateQueries({ queryKey: ['usuario'] });
    // El detalle del cliente muestra su gente: al crear una cuenta ahí, la lista
    // de ese cliente también cambió.
    void qc.invalidateQueries({ queryKey: ['cliente-trabajadores'] });
  };
}

export function useGuardarUsuario() {
  const invalidar = usarInvalidacion();

  const crear = useMutation({
    mutationFn: (datos: EntradaCrearUsuario) =>
      api.post<RespuestaInvitacion>('/api/usuarios', datos),
    onSuccess: invalidar,
  });

  const editar = useMutation({
    mutationFn: (v: { id: number; cambios: EntradaEditarUsuario }) =>
      api.patch<Usuario>(`/api/usuarios/${v.id}`, v.cambios),
    onSuccess: invalidar,
  });

  const reinvitar = useMutation({
    mutationFn: (id: number) => api.post<RespuestaInvitacion>(`/api/usuarios/${id}/invitacion`),
    onSuccess: invalidar,
  });

  /** Reenvío masivo. Ver por qué en `esquemaReenvioMasivo`. */
  const reinvitarVarias = useMutation({
    mutationFn: (ids: number[]) => api.post<ResultadoReenvio>('/api/usuarios/invitaciones', { ids }),
    onSuccess: invalidar,
  });

  return { crear, editar, reinvitar, reinvitarVarias };
}

/**
 * Activación de la cuenta con el enlace de la invitación.
 *
 * No usa `useMutation` con invalidación porque **quien la llama todavía no tiene
 * sesión**: no hay ninguna consulta en caché que refrescar.
 */
export function useActivarCuenta() {
  return useMutation({
    mutationFn: (v: { token: string; contrasena: string }) =>
      api.post<RespuestaActivacion>('/api/auth/activar', v),
  });
}
